import type { Messages } from '../de';

export default {
  title: 'Season',
  kicker: 'Races and weekly plan',
  loadError: 'The season can’t be loaded right now.',
  retry: 'Try again',
  loading: 'Loading …',
  isoWeek: 'Wk {n}',
  thisWeek: 'This week',
  runWalk: '{run} min run, {walk} min walk · {list} min',

  head: {
    label: 'Overview',
    nextRace: 'Next race',
    nextA: 'Main goal',
    phase: 'Right now',
    none: 'None entered',
    noneA: 'No A race entered',
    noPhase: 'No grid yet',
    draft: 'Draft',
    weekKm: 'Target ~{km}',
    startsOn: 'Starts {date}',
  },
  count: {
    today: 'today',
    tomorrow: 'tomorrow',
    days: { one: 'in {count} day', other: 'in {count} days' },
    weeks: { one: 'in {count} week', other: 'in {count} weeks' },
    past: 'over',
  },
  empty: {
    title: 'No races yet',
    text: 'Enter your races. They produce a weekly grid up to the last A or B race, which you confirm first.',
    add: 'Add race',
  },

  proposal: {
    titleFirst: 'Proposal for the season',
    title: 'New proposal',
    summaryFirst: { one: '{count} week, {from} to {to}, peak training week ~{km}.', other: '{count} weeks, {from} to {to}, peak training week ~{km}.' },
    diffLabel: 'Difference per week',
    noDiff: 'Nothing changes in the weeks.',
    showAll: 'Show all {count}',
    showLess: 'Show less',
    apply: 'Apply',
    applyFirst: 'Apply season',
    applying: 'Applying …',
    dismiss: 'Dismiss',
    note: 'Applying writes drafts for this week and next. They only land as drafts in your planning, not on your watch.',
    applied: 'Applied. Drafts for two weeks are in Planning.',
    toPlanning: 'Go to planning',
    dismissed: 'Dismissed. The grid stays as it is.',
    dismissedFirst: 'Proposal dismissed. It returns once you change a race or the settings.',
    weekLine: '{week}: {changes}',
    added: 'new: {phase} ~{km}',
    removed: 'dropped',
    longRun: 'Long run {from} → {to}',
    ride: 'Ride {from} → {to}',
    strength: 'Strength {from}× per week → {to}×',
    threshold: '{times} × {minutes} min threshold',
    qualityNow: 'now {text}',
    qualityGone: 'no more {text}',
    recoveryOn: 'now recovery',
    recoveryOff: 'no longer recovery',
    runWalk: 'Run-walk {text}',
    races: 'Races: {names}',
    noRaces: 'no more races',
    other: 'Workouts adjusted',
  },

  chart: {
    title: 'Timeline',
    sub: 'Running km per week',
    subDraft: 'Running km per week, how the plan would look',
    aria: { one: 'Timeline over {count} week from {from} to {to}, at most ~{km}.', other: 'Timeline over {count} weeks from {from} to {to}, at most ~{km}.' },
    ariaRaces: ' Races: {list}.',
    ariaTable: ' All values are also in the week list.',
    plan: 'Target',
    planDraft: 'Target (draft)',
    actual: 'Actual',
    actualSoFar: 'Actual (so far)',
    recovery: 'Recovery week',
    recoveryMark: 'R',
    longRun: 'Long run',
    ride: 'Ride',
    race: 'Race',
    inclRace: 'incl. race {km}',
    raceChips: 'A, B, C: races, names in the list below',
    phases: 'Phases',
    explain: 'What does that mean?',
    explainMobile: 'Legend and “What does that mean?”',
    phaseShort: { wiedereinstieg: 'Cmbk', grundlage: 'Base', aufbau: 'Bld', spitze: 'Pk', huegel: 'Hills', tapering: 'Tap', erholung: 'Rec' },
    showTable: 'As a table',
    tipRange: '{week} · {from}–{to}',
  },

  races: {
    title: 'Races',
    sub: 'A = main goal · B = important · C = training',
    add: 'Add race',
    past: 'Past',
    edit: 'Edit race {name}',
    editShort: 'Edit',
    easy: 'easy',
    test: 'test',
    goal: 'Goal {time}',
    elevation: '{m}\u00a0m climb',
    priority: 'Priority {p}',
  },
  form: {
    addTitle: 'Add race',
    editTitle: 'Edit race',
    name: 'Name',
    namePlaceholder: 'e.g. City run',
    date: 'Date',
    distance: 'Distance in km',
    distancePlaceholder: 'e.g. 21.1',
    elevation: 'Elevation gain in m (optional)',
    elevationPlaceholder: 'e.g. 800',
    goal: 'Goal time (optional)',
    goalPlaceholder: 'h:mm:ss',
    goalHint: 'e.g. 2:07:00',
    goalRead: 'Read as {time}',
    priority: 'Priority',
    prioA: 'Main goal',
    prioB: 'Important',
    prioC: 'Training',
    prioAHint: 'Full taper and recovery afterwards',
    prioBHint: 'Short taper in race week',
    prioCHint: 'Run from training, replaces the long run',
    effort: 'How will you run it?',
    effortEasy: 'Easy',
    effortEasyHint: 'Like a long run, planned in Z2',
    effortTest: 'Test',
    effortTestHint: 'Easy first, the last 40 % at race pace',
    effortFull: 'Full',
    effortFullHint: 'At race pace, counts as checkpoint',
    saveNext: 'Save, add another race',
    saved: 'Saved: {name}',
    nameError: 'Race name is missing',
    dateError: 'Date is missing',
    distanceError: 'Enter the distance in km, e.g. 21.1',
    elevationError: 'Enter elevation as a whole number',
    goalError: 'Enter the goal time as h:mm:ss',
    deleteConfirm: 'Delete race “{name}”?',
  },

  weeks: {
    title: 'Weeks',
    titleDraft: 'Weeks (draft)',
    week: 'Week',
    phase: 'Phase',
    soll: 'Target',
    longRun: 'Long run',
    ride: 'Ride',
    strength: 'Strength',
    race: 'Race',
    actual: 'Actual',
    source: 'Target from the plan, actual from your activities (from Garmin, FIT and Strava export).',
    deltaNote: 'Δ only counts up to the last full week.',
    under: 'too little',
    over: 'too much',
    sums: 'Totals per phase',
    sumTotal: 'Total',
    sumWeeks: 'Weeks',
    bestWeek: '{km}, wk {n}/{year}',
    peakBest: 'Peak {peak}: at most 110 % of your best week ({best}), that is {limit}.',
    peakCap: 'Peak {peak}: 110 % of your best week ({best}), limited to {limit} for {target}.',
    peakNone: 'Peak {peak}. No history yet: standard value {limit} for this distance.',
    peakLittle: 'Peak {peak}. Too little history (best week {best}): standard value {limit} for this distance.',
    peakNotReached: 'Week-to-week growth does not get further by then.',
    open: 'Open {week} in planning',
    showAll: 'Show all {count}',
    showLess: 'Show from this week',
    hills: '{m}\u00a0m climbing',
    hillsDown: '{m}\u00a0m climbing, half of it downhill',
    longRide: 'Room for a long bike ride',
    driftTest: 'Drift test instead of the long run ({min} min)',
    intervals: '4 × 4 min intervals',
    strengthCount: '{count}×',
    actualKm: 'Actual {km}',
    rideShort: 'Ride {text}',
    longShort: 'Long run {text}',
    strengthShort: 'Strength {count}×',
  },

  checkpoint: {
    title: 'Checkpoint',
    intro: '{race} on {date} shows where you stand before {target}.',
    lineFull: 'Checkpoint before {target}: guide time {time}, matching {goal} (Riegel formula).',
    lineNoGoal: 'Checkpoint before {target}. With a goal time, the guide time appears here.',
    waiting: 'No result for this race yet.',
    result: 'Result: {time} ({source})',
    official: 'official',
    watch: 'watch',
    projected: 'Projected to {target} with the Riegel formula: {time}. This only holds if you ran the race in full.',
    slower: 'The goal time {goal} no longer looks realistic right now, more like {suggested}. Nothing changes here; edit the race to change it.',
    edit: 'Edit race',
  },

  settings: {
    title: 'Settings',
    unsaved: 'Nothing saved yet: the defaults apply. Applying saves them too.',
    runs: 'Runs per week',
    runsHint: 'Always 3 in the comeback, this number afterwards.',
    longDay: 'Long run day',
    rideDay: 'Ride day',
    rideMin: 'Ride duration in min (0 = no ride)',
    strength: 'Strength per week',
    mobility: 'Plan mobility',
    intervals: '4×4 intervals in the build',
    intervalsHint: 'Optional, off by default. When on, the sessions are fixed in the build: 4×4 min hard, replacing part of the threshold work there.',
    strengthA: 'Strength, session A: exercises',
    strengthB: 'Strength, session B: exercises',
    strengthHint: 'Preset for weights and a bench. Sets, jumps and progression come from the plan, the hill block adds step-ups or step-downs; empty means the preset.',
    comebackWeeks: 'Comeback in weeks (0–6)',
    comebackFrom: 'Comeback from',
    rideHint: 'Long rides only in recovery weeks, not the day before the long run.',
    rideError: 'Enter the ride duration as a whole number from 0 to 480',
    saved: 'Saved. If this changes anything, a new proposal appears above.',
  },

  phaseHelp: {
    wiedereinstieg: 'Running and walking in turns, by time. Your body gets used to running again.',
    grundlage: 'Calm kilometres: the volume grows, almost everything stays easy.',
    aufbau: 'Plus one faster session a week.',
    spitze: 'The biggest weeks before the main goal, with sections at race pace.',
    huegel: 'Uphill efforts and a long run with climbing before the hill race.',
    tapering: 'Easing off before the race, so you start fresh.',
    erholung: 'After the race little and easy only, until your legs feel like it again.',
    recovery: 'Every fourth week and after longer races a little less, so the training can take effect.',
    longRun: 'Your longest run of the week.',
    sollIst: 'Target is the plan, actual is what your activities show.',
  },
  care: {
    painTitle: 'Niggling pain?',
    pain: 'Replace the session with walking or riding. Pain that grows or stays while running: stop and get it checked. The app does not diagnose.',
    missedTitle: 'Missed a week?',
    missed: 'Do not make it up. The plan suggests the adjustment itself, you confirm it.',
  },

  rules: {
    title: 'Which rules?',
    hint: 'Training recommendation, not medical advice. Pain comes first.',
  },
} satisfies Messages['season'];
